import clsx from 'clsx';
import React, { ReactNode } from 'react';

import { getFileIcon } from '@/src/components/common/Elements/Icon/FIleIcons';
import Icon from '@/src/components/common/Elements/Icon/Icon';
import LongTextByWidth from '@/src/components/common/Elements/LongText/LongTextByWidth';
import FileCard from '@/src/components/project/Inbox2/Elements/FileCard/FileCard';
import StatusBadge from '@/src/components/project/Inbox2/Elements/StatusBadge/StatusBadge';
import { EmailFileEntry } from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';

import UpdateEmailFileStatus from '../../EmailFileEntryViewer/UpdateEmailFileStatus/UpdateEmailFileStatus';

const getStatusComponent = ({
  isReadonly,
  isTrash,
  isSentEmailType,
  emailFileEntry,
  inboxEntryId,
}: {
  isReadonly: boolean;
  isTrash: boolean;
  isSentEmailType: boolean;
  emailFileEntry: EmailFileEntry;
  inboxEntryId: number;
}) => {
  if (isTrash || isSentEmailType || !emailFileEntry.file.status) {
    return null;
  }

  return isReadonly ? (
    <div className={'tw-flex tw-items-center'}>
      <StatusBadge
        status={emailFileEntry.file.status}
        isDuplicate={emailFileEntry.file.duplicate}
        isDisabled={true}
        isArrowVisible={!emailFileEntry.file.duplicate}
      />
    </div>
  ) : (
    <UpdateEmailFileStatus
      fileEntry={emailFileEntry.file}
      emailId={emailFileEntry.emailId}
      inboxEntryId={inboxEntryId}
    />
  );
};

type AttachmentItemProps = {
  emailFileEntry: EmailFileEntry;
  inboxEntryId: number;
  isSentEmailType: boolean;
  onClick: () => void;
  isReadonly?: boolean;
  isRead?: boolean;
  children?: ReactNode;
};

const AttachmentItem = ({
  emailFileEntry,
  inboxEntryId,
  onClick,
  children,
  isSentEmailType,
  isReadonly = false,
}: AttachmentItemProps) => {
  const { file } = emailFileEntry;
  const { isTrash } = useInboxSearchParams();
  const statusComponent = getStatusComponent({
    isReadonly,
    isTrash,
    isSentEmailType,
    emailFileEntry,
    inboxEntryId,
  });
  const icon = getFileIcon(file.name);

  return (
    <FileCard
      onClick={onClick}
      key={file.id}
      className={clsx(
        'tw-group',
        'tw-justify-between',
        'tw-w-full',
        'tw-p-[10px] tw-mb-[5px] tw-mr-0',
        file.isRead && 'tw-bg-cfo-grey-100'
      )}
    >
      <div
        className={clsx(
          'tw-flex tw-items-center tw-text-cfo-black',
          'group-hover:tw-text-cfo-blue-600 group-hover:tw-cursor-pointer group-hover:tw-font-brandonTextMedium'
        )}
      >
        <Icon icon={icon} className={clsx('icon--emailFilesCard', 'tw-mr-[10px]')} />

        <div>
          {/* File Name */}
          <LongTextByWidth
            className={clsx(
              'tw-text-cfo-black tw-leading-[20px] ',
              'group-hover:tw-text-cfo-blue-600'
            )}
            id={`fn_${file.id}`}
            value={file.name}
            maxWidth={800}
            maxLines={1}
            highlightBySearchTerm={true}
          />

          {children}
        </div>
      </div>
      {statusComponent}
    </FileCard>
  );
};

export default AttachmentItem;
